import { validate as validateUUID } from "uuid";
import { computed, type Ref } from "vue";
import { type LocationQueryValue, useRoute, useRouter } from "vue-router";

/**
 * Return type of {@link useUrlState}.
 * Provides URL-backed array state with UUID validation.
 */
interface UseUrlStateReturn {
  /** Replaces the query param for a field with the given array; preserves other params. */
  updateURLState: (fieldName: string, value: LocationQueryValue[] | string[]) => Promise<void>;
  /** Returns a writable computed ref bound to a query param; filters to valid UUIDs on read. */
  makeURLArrayState: (fieldName: string) => Ref<LocationQueryValue[] | string[]>;
}

/**
 * Composable for keeping array state (e.g. selected IDs) in sync with the URL query string.
 * Values are validated as UUIDs; invalid entries are filtered out when reading from the URL.
 *
 * @returns {UseUrlStateReturn} API for URL-backed array state
 */
export const useUrlState = (): UseUrlStateReturn => {
  const route = useRoute();
  const router = useRouter();

  const updateURLState = async (fieldName: string, value: LocationQueryValue[] | string[]): Promise<void> => {
    await router.replace({
      query: {
        ...route.query,
        [fieldName]: value,
      },
    });
  };

  const makeURLArrayState = (fieldName: string): Ref<LocationQueryValue[] | string[]> => {
    return computed<LocationQueryValue[] | string[]>({
      get: (): LocationQueryValue[] | string[] => {
        const value: string | LocationQueryValue[] = route.query?.[fieldName] ?? [];

        // making sure we are always operating on arrays, not scalars
        const selections: LocationQueryValue[] | string[] = Array.isArray(value) ? value : [value];

        // validate UUIDs to prevent garbage entered into the URL from messing things up
        return selections.filter((selectedId) => validateUUID((selectedId as string) ?? ""));
      },
      set: (newSelections: LocationQueryValue[] | string[] = []) => {
        updateURLState(fieldName, newSelections);
      },
    });
  };

  return {
    updateURLState,
    makeURLArrayState,
  };
};
